// Uploading a pricing sheet: drop the file, confirm what we read.
//
// The previous version asked for the vehicle, the publisher and the rate type in
// three dropdowns. Every one of those is a fact the document states and the
// parser already reads, so asking was making a person do the machine's work —
// and on a tenant with an empty registry those dropdowns were EMPTY, which made
// the feature unusable by exactly the people it should serve first.
//
// So: one file input, then a single screen of findings to confirm. Each finding
// says where it came from, because "the sheet's own contractor label" and
// "suggested by a model" deserve different amounts of trust, and a person cannot
// weigh a claim whose provenance is hidden.
//
// Everything on that screen is a WRITE being approved — creating the vehicle,
// creating the company, seeding the labor-category roster — and it is labelled in
// those words. A cold-start upload can legitimately create a couple of hundred
// catalog rows, and nobody should find that out afterwards.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton, SpecraCheckboxField, SpecraInlineBanner, SpecraOverlayDialog, SpecraSpinner } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { useUploadArtifacts } from '../../../hooks/useDeliveryDocuments';
import { govconClient } from '../../../lib/connect/clients';
import { useTenantId } from '../_shared/hooks/useTenantId';
import { useInspectPricingSheet } from './hooks';
// What the parser reads natively, plus what the convert lane handles.
const ACCEPT = '.xlsx,.xls,.csv,.html,.htm,.pdf,.docx,.doc,' + 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,' + 'application/vnd.ms-excel,text/csv,text/html,application/pdf';
const RATE_TYPE_LABEL = {
    CEILING: 'Ceilings — nobody may charge more',
    FLOOR: 'Floors — nobody may pay less',
    PROPOSED: 'Proposed rates',
    AWARDED: 'Awarded rates',
    SUBK_AGREED: 'Subcontract-agreed rates'
};
export function PricingSheetUploadDialog({ onOpenChange, onStarted, open }) {
    const companyId = useTenantId();
    const upload = useUploadArtifacts();
    const inspect = useInspectPricingSheet();
    const inputRef = useRef(null);
    const [file, setFile] = useState(null);
    // Read once, used twice: the inspection and the ingest see the same bytes.
    const [bytes, setBytes] = useState(null);
    const [found, setFound] = useState(null);
    const [seedCategories, setSeedCategories] = useState(true);
    const [starting, setStarting] = useState(false);
    const reset = ()=>{
        setFile(null);
        setBytes(null);
        setFound(null);
        setSeedCategories(true);
        setStarting(false);
    };
    const onPick = async (e)=>{
        const picked = e.target.files?.[0];
        e.target.value = '';
        if (!picked) {
            return;
        }
        setFile(picked);
        const read = new Uint8Array(await picked.arrayBuffer());
        setBytes(read);
        const result = await inspect.mutateAsync({
            bytes: read,
            companyId,
            filename: picked.name,
            mimeType: picked.type
        }).catch(()=>null);
        if (result) {
            setFound(result);
        }
    };
    const start = async ()=>{
        if (!file || !found || !bytes) {
            return;
        }
        setStarting(true);
        try {
            // The artifact first: it is what every rate points back to.
            const uploaded = await upload.mutateAsync({
                files: [
                    file
                ]
            });
            const artifact = uploaded.files[0];
            if (!artifact) {
                toast.error(uploaded.failed[0]?.error ?? 'The file could not be uploaded');
                return;
            }
            // Resolve identity BEFORE the ingest, because the company scopes every
            // labor-category entry the sheet seeds. Creating 54 entries with no company
            // is what made the registry useless: the same title from a second
            // contractor on the same vehicle collides with the first.
            // MATCHED rows only. This used to CREATE an unmatched vehicle and company
            // right here, before the document was ever read — and the preflight of a
            // PDF reads nothing but the file name, so a real registry ended up holding
            // a company called "Booz Allen": a model's guess at a filename, no UEI, no
            // review, created silently while the dialog said "Looks right — read it".
            // The ingest then read the actual cover — "Booz Allen Hamilton" — and its
            // better answer was pre-empted, because extraction correctly never proposes
            // what the caller already supplied.
            //
            // An unmatched vehicle or company is now left EMPTY, and the extraction
            // stages it as a reviewed proposal with everything else it reads — the cold
            // start ladder (vehicle → contractor → categories → rates) exists for
            // exactly this. A catalog row is shared data; it waits for a human.
            const vehicleId = found.vehicle?.matchedId ?? '';
            const companyId2 = found.company?.matchedId ?? '';
            const resp = await govconClient.extractRateObservations({
                fileData: bytes,
                filename: file.name,
                mimeType: file.type,
                tenantId: companyId,
                contractVehicleId: vehicleId,
                companyProfileId: companyId2,
                sourceDocumentId: artifact.artifactId,
                observationType: found.observationType,
                seedMissingCategories: seedCategories,
                background: true
            });
            if (!resp.runId) {
                toast.error('The ingest started but reported no run to follow');
                return;
            }
            onOpenChange(false);
            reset();
            onStarted(resp.runId, file.name);
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Could not start the ingest');
        } finally{
            setStarting(false);
        }
    };
    return /*#__PURE__*/ _jsxs(SpecraOverlayDialog, {
        description: found ? 'Check what we read. Nothing enters the rate book until you review and commit it.' : 'Drop in a rate sheet and we will read what it says about itself.',
        onOpenChange: (next)=>{
            onOpenChange(next);
            if (!next) {
                reset();
            }
        },
        open: open,
        size: "lg",
        testId: Testids.pricingSheetReview.uploadDialog,
        title: "Upload a pricing sheet",
        children: [
            /*#__PURE__*/ _jsx("input", {
                accept: ACCEPT,
                className: "hidden",
                "data-testid": Testids.pricingSheetReview.uploadInput,
                onChange: onPick,
                ref: inputRef,
                type: "file"
            }),
            inspect.isPending ? /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-2 py-6",
                children: [
                    /*#__PURE__*/ _jsx(SpecraSpinner, {}),
                    /*#__PURE__*/ _jsxs("span", {
                        children: [
                            "Reading ",
                            file?.name,
                            "…"
                        ]
                    })
                ]
            }) : found ? /*#__PURE__*/ _jsxs("div", {
                className: "space-y-4",
                children: [
                    /*#__PURE__*/ _jsx(Finding, {
                        detail: [
                            found.vehicleAgency,
                            found.vehicleType
                        ].filter(Boolean).join(' · '),
                        creatable: true,
                        label: "Contract vehicle",
                        matchedName: found.vehicle?.matchedName ?? '',
                        missing: "Nothing recognised — you can still proceed, and the ingest records what the document says",
                        name: found.vehicle?.value ?? '',
                        source: found.vehicle?.source ?? '',
                        testId: Testids.pricingSheetReview.foundVehicle
                    }),
                    /*#__PURE__*/ _jsx(Finding, {
                        detail: [
                            found.uei && `UEI ${found.uei}`,
                            found.cage && `CAGE ${found.cage}`
                        ].filter(Boolean).join(' · '),
                        creatable: true,
                        label: "Published by",
                        matchedName: found.company?.matchedName ?? '',
                        missing: // An unread document says NOTHING about its publisher, and claiming
                        // "the government" from a non-observation is how a company's own
                        // MAS price list got labelled a government rate book.
                        found.issuerKind === 'GOVERNMENT' ? 'The government — this sheet names no single contractor, so it covers many' : 'Not known yet — this document has to be converted before its publisher can be read',
                        name: found.company?.value ?? '',
                        source: found.company?.source ?? '',
                        testId: Testids.pricingSheetReview.foundCompany
                    }),
                    /*#__PURE__*/ _jsx(Finding, {
                        detail: found.contractNumber ? `Contract ${found.contractNumber}` : '',
                        label: "Kind of rates",
                        matchedName: "",
                        missing: "",
                        name: RATE_TYPE_LABEL[found.observationType] ?? found.observationType,
                        source: found.observationTypeWhy,
                        testId: Testids.pricingSheetReview.foundRateType
                    }),
                    found.needsConversion ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                        tone: "neutral",
                        children: "This is not a spreadsheet, so the above came from the file name and the contract number in it — not from the page. The document's own words are read during the ingest, which is the slow step, and anything it states there wins."
                    }) : null,
                    /*#__PURE__*/ _jsx("div", {
                        className: "rounded-[var(--radius-sm)] border border-border p-3",
                        children: /*#__PURE__*/ _jsx(SpecraCheckboxField, {
                            controlProps: {
                                checked: seedCategories,
                                onCheckedChange: (v)=>setSeedCategories(v === true)
                            },
                            "data-testid": Testids.pricingSheetReview.seedCategories,
                            description: "A rate has to attach to a labor category. Without this, any category the sheet names that your registry does not have is filed for review instead — and on a new registry that means no rates at all.",
                            label: "Create labor categories this sheet names but your registry does not have"
                        })
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex justify-end gap-2",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                onClick: reset,
                                variant: "outline",
                                children: "Pick a different file"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                "data-testid": Testids.pricingSheetReview.uploadConfirm,
                                disabled: starting,
                                onClick: start,
                                children: starting ? 'Starting…' : 'Looks right — read it'
                            })
                        ]
                    })
                ]
            }) : /*#__PURE__*/ _jsxs("div", {
                className: "space-y-4 py-2",
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-[length:var(--fs-ui-body)] text-muted-foreground",
                        children: "A vehicle rate book, a company's own schedule, a wage determination — as a spreadsheet, CSV, HTML snapshot or PDF. We read the vehicle, the publisher and what kind of rates it holds from the document itself, then ask you to confirm."
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": Testids.pricingSheetReview.uploadChoose,
                        onClick: ()=>inputRef.current?.click(),
                        children: "Choose a file"
                    })
                ]
            })
        ]
    });
}
/** One thing the document said, what it matched, and where it came from. */ function Finding({ creatable = false, detail, label, matchedName, missing, name, source, testId }) {
    const isNew = creatable && Boolean(name) && !matchedName;
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": testId,
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "text-[length:var(--fs-ui-caption)] uppercase tracking-[0.07em] text-muted-foreground",
                children: label
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "mt-1 flex items-center gap-2",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "text-[length:var(--fs-ui-body)] font-medium",
                        children: matchedName || name || missing
                    }),
                    matchedName ? /*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: "neutral",
                        children: "Already in your registry"
                    }) : isNew ? // Named as a write, not as a status — and as the write it actually is:
                    // a staged proposal a reviewer accepts, never a row minted from a
                    // preflight guess.
                    /*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: "info",
                        children: "Will be proposed for review"
                    }) : null
                ]
            }),
            detail ? /*#__PURE__*/ _jsx("div", {
                className: "text-[length:var(--fs-ui-caption)] text-muted-foreground",
                children: detail
            }) : null,
            source ? // The provenance of the claim. Without it a person cannot tell a labelled
            // cell from a filename guess, and both look equally confident.
            /*#__PURE__*/ _jsxs("div", {
                className: "text-[length:var(--fs-ui-caption)] text-muted-foreground",
                children: [
                    "Read from ",
                    source
                ]
            }) : null
        ]
    });
}
